// Created by Weverton Couto
// on 23/10/2025
// for Techmatic Solutions
// using Odoo 17.0

import URL_API from '../../variaveis_globais/const';
import { FormatarDateParaDDMMYYYY } from '../../utils/utils';
import VariaveisGlobais from 'variaveis_globais/variaveis_globais';

const axios = require('axios');

// Get all clinics
export const getTodasClinicas = function() {
    return new Promise((resolve, reject) => {
        axios.get(`${URL_API}clinica/todas`, {
            headers: { Authorization: `Bearer ${VariaveisGlobais.token}` }
        }).then(function (response) {
            if (response.data.error == true) reject(true);
            resolve(response.data.data || []);
        }).catch(function(error) {
            console.log(error);
            reject(error);
        });
    });
};

// Get messages for a specific clinic
export const getMensagensPorClinica = function(idClinica, tipo, dataInicio, dataFim, idEnviadoPara, idEnviadoPor) {
    let query = `idClinica=${idClinica}`;
    if (tipo) query += `&tipo=${tipo}`;
    if (dataInicio) query += `&dataInicio=${FormatarDateParaDDMMYYYY(dataInicio)}`;
    if (dataFim) query += `&dataFim=${FormatarDateParaDDMMYYYY(dataFim)}`;
    if (idEnviadoPara) query += `&idEnviadoPara=${idEnviadoPara}`;
    if (idEnviadoPor) query += `&idEnviadoPor=${idEnviadoPor}`;
    
    return new Promise((resolve, reject) => {
        axios.get(`${URL_API}mensagem?${query}`, {
            headers: { Authorization: `Bearer ${VariaveisGlobais.token}` }
        }).then(function (response) {
            if (response.data.error == true) reject(true);
            resolve(response.data.data || []);
        }).catch(function(error) {
            console.log(`Error fetching messages for clinic ${idClinica}:`, error);
            resolve([]); // Return empty array instead of rejecting to continue processing other clinics
        });
    });
};

// Get statistics for a specific day
export const getEstatisticasDia = async function(data, idClinica) {
    try {
        const clinicas = await getTodasClinicas();
        const clinicasFiltradas = idClinica 
            ? clinicas.filter(c => c.idGsDoctor === idClinica.toString())
            : clinicas;

        const inicioDia = new Date(data);
        inicioDia.setHours(0, 0, 0, 0);
        const fimDia = new Date(data);
        fimDia.setHours(23, 59, 59, 999);

        const promises = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, inicioDia, fimDia)
                .then(mensagens => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    mensagens: mensagens
                }))
                .catch(() => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    mensagens: []
                }))
        );

        const resultados = await Promise.all(promises);

        const estatisticas = resultados.map(resultado => {
            const porTipo = {};
            resultado.mensagens.forEach(msg => {
                const tipo = msg.tipo || 'Sem tipo';
                porTipo[tipo] = (porTipo[tipo] || 0) + 1;
            });

            return {
                idClinica: resultado.idClinica,
                nome: resultado.nome,
                total: resultado.mensagens.length,
                porTipo: porTipo
            };
        });

        const totalGeral = estatisticas.reduce((sum, e) => sum + e.total, 0);

        return {
            data: FormatarDateParaDDMMYYYY(data),
            totalGeral: totalGeral,
            clinicas: estatisticas
        };
    } catch (error) {
        console.error('Error getting daily statistics:', error);
        throw error;
    }
};

// Get statistics for a specific month
export const getEstatisticasMes = async function(ano, mes, idClinica) {
    try {
        const clinicas = await getTodasClinicas();
        const clinicasFiltradas = idClinica 
            ? clinicas.filter(c => c.idGsDoctor === idClinica.toString())
            : clinicas;

        const inicioMes = new Date(ano, mes, 1);
        inicioMes.setHours(0, 0, 0, 0);
        const fimMes = new Date(ano, mes + 1, 0);
        fimMes.setHours(23, 59, 59, 999);

        const promises = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, inicioMes, fimMes)
                .then(mensagens => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    mensagens: mensagens
                }))
                .catch(() => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    mensagens: []
                }))
        );

        const resultados = await Promise.all(promises);

        const estatisticas = resultados.map(resultado => {
            const porTipo = {};
            const porDia = {};

            resultado.mensagens.forEach(msg => {
                const tipo = msg.tipo || 'Sem tipo';
                porTipo[tipo] = (porTipo[tipo] || 0) + 1;

                if (msg.criadoEm) {
                    const dataMsg = new Date(msg.criadoEm);
                    const dia = dataMsg.getDate();
                    porDia[dia] = (porDia[dia] || 0) + 1;
                }
            });

            const porDiaArray = Object.keys(porDia).map(dia => ({
                dia: parseInt(dia),
                total: porDia[dia]
            })).sort((a, b) => a.dia - b.dia);

            return {
                idClinica: resultado.idClinica,
                nome: resultado.nome,
                total: resultado.mensagens.length,
                porTipo: porTipo,
                porDia: porDiaArray
            };
        });

        const totalGeral = estatisticas.reduce((sum, e) => sum + e.total, 0);

        return {
            ano: ano,
            mes: mes + 1,
            totalGeral: totalGeral,
            clinicas: estatisticas
        };
    } catch (error) {
        console.error('Error getting monthly statistics:', error);
        throw error;
    }
};

// Get statistics for last 12 months
export const getEstatisticas12Meses = async function(idClinica) {
    try {
        const clinicas = await getTodasClinicas();
        const clinicasFiltradas = idClinica 
            ? clinicas.filter(c => c.idGsDoctor === idClinica.toString())
            : clinicas;

        const hoje = new Date();
        const inicio12Meses = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1);
        inicio12Meses.setHours(0, 0, 0, 0);
        const fim12Meses = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        fim12Meses.setHours(23, 59, 59, 999);

        const promises = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, inicio12Meses, fim12Meses)
                .then(mensagens => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    mensagens: mensagens
                }))
                .catch(() => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    mensagens: []
                }))
        );

        const resultados = await Promise.all(promises);

        const porMes = {};
        const mesesNomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        
        for (let i = 0; i < 12; i++) {
            const dataRef = new Date(hoje.getFullYear(), hoje.getMonth() - (11 - i), 1);
            const mesKey = `${dataRef.getFullYear()}-${dataRef.getMonth()}`;
            porMes[mesKey] = {
                mes: mesesNomes[dataRef.getMonth()],
                mesNumero: dataRef.getMonth() + 1,
                ano: dataRef.getFullYear(),
                total: 0
            };
        }

        resultados.forEach(resultado => {
            resultado.mensagens.forEach(msg => {
                if (msg.criadoEm) {
                    const dataMsg = new Date(msg.criadoEm);
                    const mesKey = `${dataMsg.getFullYear()}-${dataMsg.getMonth()}`;
                    if (porMes[mesKey]) {
                        porMes[mesKey].total += 1;
                    }
                }
            });
        });

        const dadosArray = Object.values(porMes).sort((a, b) => {
            if (a.ano !== b.ano) return a.ano - b.ano;
            return a.mesNumero - b.mesNumero;
        });

        const totalGeral = dadosArray.reduce((sum, m) => sum + m.total, 0);

        return {
            meses: dadosArray,
            totalGeral: totalGeral
        };
    } catch (error) {
        console.error('Error getting 12 months statistics:', error);
        throw error;
    }
};

// Get messages for a specific day with pagination
export const getMensagensDia = async function(data, idClinica, page = 1, limit = 25) {
    try {
        const clinicas = await getTodasClinicas();
        const clinicasFiltradas = idClinica 
            ? clinicas.filter(c => c.idGsDoctor === idClinica.toString())
            : clinicas;

        const inicioDia = new Date(data);
        inicioDia.setHours(0, 0, 0, 0);
        const fimDia = new Date(data);
        fimDia.setHours(23, 59, 59, 999);

        const promises = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, inicioDia, fimDia)
                .then(mensagens => mensagens.map(msg => ({
                    ...msg,
                    nomeClinica: clinica.nome,
                    idClinica: clinica.idGsDoctor
                })))
                .catch(() => [])
        );

        const resultados = await Promise.all(promises);
        const todasMensagens = resultados.flat();

        todasMensagens.sort((a, b) => {
            const dataA = new Date(a.criadoEm || 0);
            const dataB = new Date(b.criadoEm || 0);
            return dataB - dataA;
        });

        const total = todasMensagens.length;
        const inicio = (page - 1) * limit;
        const fim = inicio + limit;
        const mensagensPaginas = todasMensagens.slice(inicio, fim);

        return {
            mensagens: mensagensPaginas,
            paginacao: {
                pagina: page,
                limite: limit,
                total: total,
                totalPaginas: Math.ceil(total / limit)
            }
        };
    } catch (error) {
        console.error('Error getting daily messages:', error);
        throw error;
    }
};

// Get top 5 clinics by message volume
export const getTop5Clinicas = async function(periodo = 'mes', idClinica) {
    try {
        const clinicas = await getTodasClinicas();
        const hoje = new Date();
        let dataInicio, dataFim;

        if (periodo === 'mes') {
            dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        } else if (periodo === '12meses') {
            dataInicio = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1);
            dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        } else {
            dataInicio = new Date(hoje);
            dataFim = new Date(hoje);
        }

        dataInicio.setHours(0, 0, 0, 0);
        dataFim.setHours(23, 59, 59, 999);

        const clinicasFiltradas = idClinica 
            ? clinicas.filter(c => c.idGsDoctor === idClinica.toString())
            : clinicas;

        const promises = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, dataInicio, dataFim)
                .then(mensagens => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    total: mensagens.length
                }))
                .catch(() => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    total: 0
                }))
        );

        const resultados = await Promise.all(promises);
        const top5 = resultados
            .sort((a, b) => b.total - a.total)
            .slice(0, 5)
            .filter(c => c.total > 0);

        return top5;
    } catch (error) {
        console.error('Error getting top 5 clinics:', error);
        throw error;
    }
};

// Calculate daily average for a period
export const getMediaDiaria = async function(periodo = 'mes', idClinica) {
    try {
        const hoje = new Date();
        let dataInicio, dataFim, dias;

        if (periodo === 'mes') {
            dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
            dias = dataFim.getDate();
        } else if (periodo === '12meses') {
            dataInicio = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1);
            dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
            dias = 365;
        } else {
            dataInicio = new Date(hoje);
            dataFim = new Date(hoje);
            dias = 1;
        }

        dataInicio.setHours(0, 0, 0, 0);
        dataFim.setHours(23, 59, 59, 999);

        const clinicas = await getTodasClinicas();
        const clinicasFiltradas = idClinica 
            ? clinicas.filter(c => c.idGsDoctor === idClinica.toString())
            : clinicas;

        const promises = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, dataInicio, dataFim)
                .then(mensagens => mensagens.length)
                .catch(() => 0)
        );

        const resultados = await Promise.all(promises);
        const total = resultados.reduce((sum, v) => sum + v, 0);
        const media = dias > 0 ? (total / dias).toFixed(1) : 0;

        return parseFloat(media);
    } catch (error) {
        console.error('Error calculating daily average:', error);
        throw error;
    }
};

// Calculate variation percentage between two periods
export const getVariacaoPeriodo = async function(periodoAtual, periodoAnterior, idClinica) {
    try {
        const hoje = new Date();
        let dataInicioAtual, dataFimAtual, dataInicioAnterior, dataFimAnterior;

        if (periodoAtual === 'dia') {
            dataInicioAtual = new Date(hoje);
            dataFimAtual = new Date(hoje);
            const ontem = new Date(hoje);
            ontem.setDate(ontem.getDate() - 1);
            dataInicioAnterior = ontem;
            dataFimAnterior = ontem;
        } else if (periodoAtual === 'mes') {
            dataInicioAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            dataFimAtual = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
            dataInicioAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
            dataFimAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
        } else {
            dataInicioAtual = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1);
            dataFimAtual = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
            dataInicioAnterior = new Date(hoje.getFullYear() - 1, hoje.getMonth() - 11, 1);
            dataFimAnterior = new Date(hoje.getFullYear() - 1, hoje.getMonth() + 1, 0);
        }

        dataInicioAtual.setHours(0, 0, 0, 0);
        dataFimAtual.setHours(23, 59, 59, 999);
        dataInicioAnterior.setHours(0, 0, 0, 0);
        dataFimAnterior.setHours(23, 59, 59, 999);

        const clinicas = await getTodasClinicas();
        const clinicasFiltradas = idClinica 
            ? clinicas.filter(c => c.idGsDoctor === idClinica.toString())
            : clinicas;

        const promisesAtual = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, dataInicioAtual, dataFimAtual)
                .then(mensagens => mensagens.length)
                .catch(() => 0)
        );

        const promisesAnterior = clinicasFiltradas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, dataInicioAnterior, dataFimAnterior)
                .then(mensagens => mensagens.length)
                .catch(() => 0)
        );

        const [totaisAtual, totaisAnterior] = await Promise.all([
            Promise.all(promisesAtual),
            Promise.all(promisesAnterior)
        ]);

        const totalAtual = totaisAtual.reduce((sum, v) => sum + v, 0);
        const totalAnterior = totaisAnterior.reduce((sum, v) => sum + v, 0);

        if (totalAnterior === 0) {
            return totalAtual > 0 ? 100 : 0;
        }

        const variacao = ((totalAtual - totalAnterior) / totalAnterior) * 100;
        return parseFloat(variacao.toFixed(1));
    } catch (error) {
        console.error('Error calculating period variation:', error);
        throw error;
    }
};

// Get clinics with messages for a specific month
export const getClinicasPorMes = async function(ano, mes) {
    try {
        const clinicas = await getTodasClinicas();
        
        const inicioMes = new Date(ano, mes, 1);
        inicioMes.setHours(0, 0, 0, 0);
        const fimMes = new Date(ano, mes + 1, 0);
        fimMes.setHours(23, 59, 59, 999);

        const promises = clinicas.map(clinica => 
            getMensagensPorClinica(clinica.idGsDoctor, undefined, inicioMes, fimMes)
                .then(mensagens => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    quantidade: mensagens.length,
                    valorUnitario: 0.14,
                    valorTotal: parseFloat((mensagens.length * 0.14).toFixed(2))
                }))
                .catch(() => ({
                    idClinica: clinica.idGsDoctor,
                    nome: clinica.nome,
                    quantidade: 0,
                    valorUnitario: 0.14,
                    valorTotal: 0
                }))
        );

        const resultados = await Promise.all(promises);
        
        // Filter only clinics with at least one message
        const clinicasComMensagens = resultados
            .filter(c => c.quantidade > 0)
            .sort((a, b) => b.quantidade - a.quantidade);

        const totalGeral = clinicasComMensagens.reduce((sum, c) => sum + c.quantidade, 0);
        const valorTotalGeral = clinicasComMensagens.reduce((sum, c) => sum + c.valorTotal, 0);

        return {
            ano: ano,
            mes: mes + 1,
            mesNome: new Date(ano, mes).toLocaleString('pt-BR', { month: 'long', year: 'numeric' }),
            clinicas: clinicasComMensagens,
            totalGeral: totalGeral,
            valorTotalGeral: parseFloat(valorTotalGeral.toFixed(2))
        };
    } catch (error) {
        console.error('Error getting clinics by month:', error);
        throw error;
    }
};

